import { DOCUMENT } from '@angular/common';
import { Component, ElementRef, Inject, OnDestroy, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { fromEvent } from 'rxjs/internal/observable/fromEvent';
import { first } from 'rxjs/operators';
import { Task } from '../../helpers/task';
import { ReturnUrlInfo } from '../../internals/return-url-info';
import { TcmApplicationManagerService } from '../../internals/tcm-application-manager.service';
import { RenewPasswordViewModel } from '../../models/logins';
import { LoginViewModel } from '../../models/logins/login-view-model';
import { AuthenticationResult, LoginApiService } from '../../services/system/login-api.service';
import { TranslationService } from '../../services/translation.service';
import { TcmModalService } from '../../modules/tcm-common/services/tcm-modal.service';
import { TcmImagesSystemService } from '../../internals/tcm-images-system-service';
import { TCMFormHelper } from '../../modules/tcm-core.module/classes/tcm-form-helper';
import { NavigationService } from '../../modules/tcm-core.module/services/navigation.service';
import { Deferred } from '../../modules/tcm-core.module/helpers/deferred';
import { OperationCanceledException } from '../../modules/tcm-core.module/exceptions/operation-canceled-exception';


@Component({
    selector: 'login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    // imports: [CommonModule, TCMFormModule],
    standalone: false,
})
export class LoginComponent implements OnDestroy {

  static get instance() {
    return LoginComponent._instance;
  }

  readonly isModal: boolean;

  user = {} as LoginViewModel;

  renewPass: {
    newPassword: string,
    confirmPassword: string
  } = {} as any;

  userMessage: { isSuccess: boolean, message: any };

  step: SignInStep;

  isLoading: boolean = false;

  readonly SignInStepEnum = SignInStep;


  @ViewChild(NgForm, { static: true }) private ngForm: NgForm;
  @ViewChild(NgForm, { static: true, read: ElementRef }) private ngFormElementRef: ElementRef<HTMLFormElement>;
  @ViewChild("passwordBox", { static: true }) private passwordBoxElementRef: ElementRef<HTMLElement>;
  @ViewChild("passwordConfirmBox", { static: true }) private passwordConfirmBoxElementRef: ElementRef<HTMLElement>;

  private returnUrlInfo: ReturnUrlInfo;

  static _instance: LoginComponent;

  private componentCancellationTokenSource = new Deferred();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly navigator: NavigationService,
    private readonly loginService: LoginApiService,
    private readonly translationService: TranslationService,
    private readonly applicationManagerService: TcmApplicationManagerService,
    private readonly modalService: TcmModalService,
    public imagesService: TcmImagesSystemService,
    @Inject(DOCUMENT) private readonly document: Document) {

    if (LoginComponent._instance) {
      throw 'Login Component already opened';
    }
    LoginComponent._instance = this;

    this.isModal = !route.component || !(this instanceof (route.component as any));

    const params = this.route.snapshot.queryParams['params'];

    this.returnUrlInfo = params ? JSON.parse(params) : null;

    this.step = SignInStep.Email;

    // this.user.email = 'admin@localhost.com';
  }

  ngAfterViewInit() {
    this.focusOnInput("waf-email-input", "email");
  }

  ngOnDestroy(): void {
    LoginComponent._instance = null;

    this.componentCancellationTokenSource.resolve();
  }

  async close() {

    this.componentCancellationTokenSource.resolve();

    if (this.isModal) {
      this.modalService.closeModalByComponentRef(this);
    }
    else {

      try {
        this.isLoading = true;

        const returnUrl = this.returnUrlInfo ? this.returnUrlInfo.returnUrl : '/';

        this.applicationManagerService.refreshApplicationPageIfRequired(returnUrl);
                
        await this.navigator.navigate(returnUrl, null, { replaceUrl: true });
      }
      finally {
        this.isLoading = false;
      }
    }

  }

  async onFormSubmit() {
    if (!this.ngForm.valid) {
      
      TCMFormHelper.invalidateForm(this.ngForm, this.ngFormElementRef.nativeElement, true);
      return;
    }

    this.userMessage = null;

    if (this.step == SignInStep.Email) {

      const authMethodResponse = await this.wrapRemoteCallAsync(() => this.loginService.determineAuthenticationMethodAsync(this.user.email));

      if (!authMethodResponse) {
        return;
      }
      else if (authMethodResponse.isSingleSignOn) {
        this.loginService
          .makeSingleSignOnAsync(this.user.email, this.componentCancellationTokenSource.promise())
          .catch(e => {
            if (!(e instanceof OperationCanceledException)) {
              this.userMessage = { isSuccess: false, message: e };
            }
          });
      }
      else {
        this.step = SignInStep.Password;

        this.focusOnInput('waf-password-input', 'password');

        expandSection(this.passwordBoxElementRef.nativeElement);
      }
    }
    else if (this.step == SignInStep.Password) {

      const authenticationResult = await this.wrapRemoteCallAsync(() => this.loginService.loginAsync(this.user));

      if (authenticationResult && authenticationResult == AuthenticationResult.PasswordChangeRequired) {

        this.userMessage = {
          isSuccess: false,
          message: this.translationService.translate(x => x.Screens.Login.Messages.PasswordExpired)
        };

        this.renewPass = {} as any;

        this.step = SignInStep.ChangePassword;

        this.focusOnInput('waf-password-input', 'newPassword');

        expandSection(this.passwordConfirmBoxElementRef.nativeElement);
      }
    }
    else if (this.step == SignInStep.ChangePassword) {
      const renewPasswordModel: RenewPasswordViewModel = {
        login: this.user.email,
        password: this.user.password,
        passwordNew: this.renewPass.newPassword
      };


      try {
        await this.wrapRemoteCallAsync(
          () => this.loginService.renewPasswordAsync(renewPasswordModel, this.componentCancellationTokenSource.promise()), true);

        const message = this.translationService.translate(x => x.Screens.Login.Messages.PasswordChangedSuccessfully);

        this.userMessage = {
          isSuccess: true,
          message: message
        }

        this.backToPreviousStates(true);
      }
      catch{
        this.backToPreviousStates();
      }
    }
  }

  async backToPreviousStates(keepLastMessage: boolean = false) {

    if (!keepLastMessage) {
      this.userMessage = null;
    }

    if (this.step == SignInStep.Password) {

      this.step = SignInStep.Email;

      collapseSection(this.passwordBoxElementRef.nativeElement);

      await Task.delay(600);

      const passwordControl = this.ngForm.controls["password"];

      passwordControl.reset();

      this.focusOnInput('waf-email-input', 'email');
    }
    else if (this.step == SignInStep.ChangePassword) {
      this.step = SignInStep.Password;

      collapseSection(this.passwordConfirmBoxElementRef.nativeElement);

      await Task.delay(600);

      this.renewPass = {} as any;

      this.ngForm.controls["password"].reset();
      this.ngForm.controls["newPassword"].reset();
      this.ngForm.controls["confirmPassword"].reset();

      this.focusOnInput('waf-password-input', 'password');
    }
  }

  private async wrapRemoteCallAsync<T = any>(executor: () => Promise<T>, raiseError = false): Promise<T> {
    try {

      this.isLoading = true;

      const result = await executor();

      return result;
    }
    catch (e) {

      if (!(e instanceof OperationCanceledException)) {
        this.userMessage = { isSuccess: false, message: e };
      }

      if (raiseError) {
        throw e;
      }
      else {
        return null;
      }
    }
    finally {
      this.isLoading = false;
    }
  }

  private focusOnInput(tagName: string, attr: string) {
    const input = this.ngFormElementRef.nativeElement.querySelector<HTMLInputElement>(`${tagName}[name="${attr}"] input`);

    if (input) {
      input.focus();
    }
  }
}


enum SignInStep {
  Email = 1,
  Password = 2,
  ChangePassword = 3
}

function collapseSection(element) {
  // get the height of the element's inner content, regardless of its actual size
  var sectionHeight = element.scrollHeight;

  // temporarily disable all css transitions
  var elementTransition = element.style.transition;
  element.style.transition = '';

  // on the next frame (as soon as the previous style change has taken effect),
  // explicitly set the element's height to its current pixel height, so we 
  // aren't transitioning out of 'auto'
  requestAnimationFrame(function () {
    element.style.height = sectionHeight + 'px';
    element.style.transition = elementTransition;

    // on the next frame (as soon as the previous style change has taken effect),
    // have the element transition to height: 0
    requestAnimationFrame(function () {
      element.style.height = 0 + 'px';
    });
  });
}

function expandSection(element) {
  // get the height of the element's inner content, regardless of its actual size
  var sectionHeight = element.scrollHeight;

  // have the element transition to the height of its inner content
  element.style.height = sectionHeight + 'px';


  fromEvent(element, "transitionend").pipe(first()).subscribe(e => {
    element.style.height = 'auto';
  })
}

